import { IonButton, IonContent, IonIcon, IonList, IonModal, IonNote, IonPage, IonSkeletonText, IonText, isPlatform, useIonAlert, useIonLoading, useIonPopover, IonCard, IonCardContent, IonCardHeader, IonCardTitle } from '@ionic/react';
import { formattingLocales } from '../utils/formattingLocale';
import { IonItem, IonLabel, IonItemDivider } from '@ionic/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { history as appHistory } from '../utils/history';
import { clearEventModalHref, eventModalHref, matchEventModalLocation } from '../utils/eventModalLocation';
import { HlsJsVideo } from '@videojs/react/media/hlsjs-video';
import { LoadFailureStateContainer, LoadingStateContainer } from '../components/StateContainer';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { chevronDown, chevronDownOutline, copyOutline, downloadOutline, imageOutline, lockOpenOutline, openOutline, shareOutline, shareSocial, starOutline, star, trashOutline } from 'ionicons/icons';
import OCGatewayClient from '../client/OCGatewayClient';
import { DeviceEvent } from '../models/DeviceEvent';
import type { SubEvent } from '../models/EventSummary';
import RfidChip from '../components/RfidChip';
import styles from './EventModal.module.css';
import { EVENT_VIDEO_FPS, EventVideoPlayer, EventVideoSkin, frameCenterTime, type EventVideoMedia } from './EventVideoSkin';
import { useDeviceEventQuery } from '../query/deviceEventQuery';
import { useDeviceQuery } from '../query/deviceQuery';
import { useDeviceEventDeleteMutation } from '../query/deviceEventDeleteMutation';
import { useDeviceEventsQuery } from '../query/deviceEventsQuery';
import { useDevicesQuery } from '../query/devicesQuery';
import { useGetUserStarredEventQuery } from '../query/getUserStarredEventQuery';
import { useStarEventMutation, useUnstarEventMutation } from '../query/starEventMutation';
import useUX from '../utils/ux';
import { useHistory } from 'react-router-dom';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { useTranslation } from 'react-i18next';
import EventClassificationChip from '../components/EventClassificationChip';
import EventNavCarousel from './EventNavCarousel';
import { useMediaQuery } from '../hooks/useMediaQuery';
import EventSummaryDescription from '../components/EventSummaryDescription';
import { useEventSummaryQuery } from '../query/eventSummaryQuery';

const RELATED_EVENTS_PAGE_SIZE = 10;

// The video URL carries no .m3u8 suffix, so state the content type rather
// than rely on the player's extension sniffing (module-level so the config
// prop keeps a stable identity across renders)
const HLS_VIDEO_CONFIG = { contentType: 'application/vnd.apple.mpegurl' };

// stable identity so the timeline memos don't recompute every render while
// there is no summary yet
const NO_SUBEVENTS: SubEvent[] = [];

// Regional date+time, following Timestamp.tsx: the device locale (not the app
// language) decides field order and the 12/24-hour clock
const formatEventDateTime = (date: Date) => new Intl.DateTimeFormat(formattingLocales(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
}).format(date);

// Ionic's card presentation (inset below the status bar, scale-back of the page
// behind, swipe-to-dismiss) is keyed off the modal's mode being "ios", not the
// OS. Android would otherwise present full-screen with content under the status
// bar, so force iOS mode on the modal shell there; the content re-pins md mode
// so it keeps Android styling. Desktop is unaffected either way: from 768px
// Ionic presents a centred dialog and skips the card treatment entirely.
const FORCE_CARD_MODAL = isPlatform('android');

interface EventModalProps {
    deviceId: string;
    eventId: number;
    accessToken: string | null;
    onClose: () => void;
}

// The popover contents live at module scope: the modal re-renders on every
// frame tick while a video plays, and inline components get a new identity
// per render, which remounts the open popover (losing scroll and any other
// state) on every tick.

const DevicePopover = ({ deviceId, onCopyDeviceId, onShowDevice }: {
    deviceId: string;
    onCopyDeviceId: () => void;
    onShowDevice: () => void;
}) => {
    const { t } = useTranslation('pages');

    return <IonContent>
        <IonList>
            <IonItem button detail={true} detailIcon={copyOutline} onClick={onCopyDeviceId}>
                <IonIcon slot="start" src="/assets/device/onlycat-device-icon.svg" />
                <div>
                    <IonLabel>{t('device.device_id')}</IonLabel>
                    <IonNote class="ion-text-nowrap">{deviceId}</IonNote>
                </div>
            </IonItem>
            <IonItem button onClick={onShowDevice}>
                <IonIcon slot="start" icon={openOutline} />
                <IonLabel>{t('event.show_device')}</IonLabel>
            </IonItem>
        </IonList>
    </IonContent>;
};

const SharePopover = ({ isVideoPaused, onShare, onDownloadVideo, onDownloadFrame }: {
    isVideoPaused: boolean;
    onShare: () => void;
    onDownloadVideo: () => void;
    onDownloadFrame: () => void;
}) => {
    const { t } = useTranslation('pages');

    return <IonContent>
        <IonList>
            <IonItem button detail={false} onClick={onShare}>
                <IonIcon slot="start" ios={shareOutline} md={shareSocial} />
                <IonLabel>{t('event.actions.share_video')}</IonLabel>
            </IonItem>
            <IonItem button detail={false} onClick={onDownloadVideo}>
                <IonIcon slot="start" icon={downloadOutline} />
                <IonLabel>{t('event.download_video')}</IonLabel>
            </IonItem>
            {/* only meaningful while paused, when the video is parked on the one
                frame currentFrameIndex refers to; mid-playback there is no
                single frame the user could be asking for */}
            {isVideoPaused && (
                <IonItem button detail={false} onClick={onDownloadFrame}>
                    <IonIcon slot="start" icon={imageOutline} />
                    <IonLabel>{t('event.download_frame')}</IonLabel>
                </IonItem>
            )}
        </IonList>
    </IonContent>;
};

const MoreOptionsPopover = ({ isLiveEvent, canManage, onUnlock, onShowDevice, onDeleteEvent }: {
    isLiveEvent: boolean;
    canManage: boolean;
    onUnlock: () => void;
    onShowDevice: () => void;
    onDeleteEvent: () => void;
}) => {
    const { t } = useTranslation('pages');

    return <IonContent>
        <IonList>
            {isLiveEvent && (
                <IonItem button detail={false} onClick={onUnlock} disabled={!canManage}>
                    <IonIcon slot="start" icon={lockOpenOutline} />
                    <IonLabel>{t('device.remote_unlock')}</IonLabel>
                    <span slot="end" className={styles.liveBadge}>
                        <span className={styles.liveBadgeDot} aria-hidden="true" />
                        {t('event.actions.live')}
                    </span>
                </IonItem>
            )}
            <IonItem button detail={false} onClick={onShowDevice}>
                <IonIcon slot="start" icon={openOutline} />
                <IonLabel>{t('event.show_device')}</IonLabel>
            </IonItem>
            <IonItem button detail={false} onClick={onDeleteEvent} className={styles.deleteItem} disabled={!canManage}>
                <IonIcon slot="start" icon={trashOutline} color="danger" />
                <IonLabel color="danger">{t('event.delete_event')}</IonLabel>
            </IonItem>
        </IonList>
    </IonContent>;
};

const EventVideoMediaBridge = ({ onMediaChange }: { onMediaChange: (media: EventVideoMedia) => void }) => {
    // the library types useMedia() as a narrow facade, but it hands back the
    // real element — see the EventVideoMedia declaration
    const media = EventVideoPlayer.useMedia() as EventVideoMedia;

    useEffect(() => {
        onMediaChange(media);

        return () => onMediaChange(null);
    }, [media, onMediaChange]);

    return null;
};

const EventModal: React.FC<EventModalProps> = ({ deviceId, eventId, accessToken, onClose }) => {
    const { t } = useTranslation('pages');
    const { t: tCommon, i18n: commonI18n } = useTranslation('common');
    const activeLocale = commonI18n.resolvedLanguage ?? commonI18n.language ?? 'en';
    const eventQuery = useDeviceEventQuery(deviceId, eventId, accessToken);
    const eventSummaryQuery = useEventSummaryQuery(deviceId, eventId, accessToken);
    const deviceQuery = useDeviceQuery(deviceId);
    const devicesQuery = useDevicesQuery();
    const starredQuery = useGetUserStarredEventQuery(deviceId, eventId);
    const starEventMutation = useStarEventMutation();
    const unstarEventMutation = useUnstarEventMutation();
    const { presentResult } = useUX();
    const [presentLoading, dismissLoading] = useIonLoading();
    const [presentAlert] = useIonAlert();
    const history = useHistory();
    const deleteEventMutation = useDeviceEventDeleteMutation();
    const [videoMedia, setVideoMedia] = useState<EventVideoMedia>(null);
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [isVideoPaused, setIsVideoPaused] = useState(true);
    const contentRef = useRef<HTMLIonContentElement>(null);
    const videoBlockRef = useRef<HTMLDivElement>(null);

    // Landscape snap: released drags settle onto the nearest meaningful
    // state — the full-height video or the metadata view. CSS scroll-snap
    // can't express this: it demands about half the snap distance in travel
    // before it lets go, and with snap points a full viewport apart that
    // reads as the video simply refusing to scroll.
    //
    // Top settle: fractional layout rounding leaves the content 1-2px
    // scrollable even when it fits, and resting at scrollTop 2 blocks the
    // card's swipe-to-dismiss (it requires exactly 0). Whenever scrolling
    // comes to rest inside a few px of the top, return it to 0 — genuine
    // overflow is untouched beyond that imperceptible band.
    //
    // At-top marker: data-scroll-at-top on the ion-content host scopes the
    // overscroll suppression (see the module css) to the rest-at-top state,
    // so the native rubber band stays alive everywhere else.
    useEffect(() => {
        const SNAP_THRESHOLD_PX = 64;
        const TOP_SETTLE_BAND_PX = 3;
        const SCROLL_IDLE_MS = 80;

        let scrollEl: HTMLElement | undefined;
        let disposed = false;
        let startScrollTop = 0;
        let touchActive = false;
        let settleTimer: number | undefined;

        const syncAtTopMarker = () => {
            if (scrollEl) {
                contentRef.current?.toggleAttribute('data-scroll-at-top', scrollEl.scrollTop <= 0);
            }
        };

        const settleToTop = () => {
            if (scrollEl && !touchActive && scrollEl.scrollTop > 0 && scrollEl.scrollTop <= TOP_SETTLE_BAND_PX) {
                scrollEl.scrollTop = 0;
            }

            syncAtTopMarker();
        };

        const onScroll = () => {
            syncAtTopMarker();
            window.clearTimeout(settleTimer);
            settleTimer = window.setTimeout(settleToTop, SCROLL_IDLE_MS);
        };

        const onTouchStart = () => {
            touchActive = true;
            if (scrollEl) {
                startScrollTop = scrollEl.scrollTop;
            }
        };

        // runs on release, cancelling any momentum, so the settle is
        // deterministic
        const snapLandscapeScroll = () => {
            if (!scrollEl || !window.matchMedia('(orientation: landscape) and (max-height: 599px)').matches) {
                return;
            }

            const videoHeight = videoBlockRef.current?.offsetHeight ?? 0;
            const { scrollTop } = scrollEl;

            if (videoHeight <= 0 || scrollTop <= 0 || scrollTop >= videoHeight) {
                return;
            }

            let target: number;
            if (scrollTop > startScrollTop) {
                // heading toward the metadata view
                target = scrollTop > SNAP_THRESHOLD_PX ? videoHeight : 0;
            } else if (scrollTop < startScrollTop) {
                // heading toward the full-height video
                target = scrollTop < videoHeight - SNAP_THRESHOLD_PX ? 0 : videoHeight;
            } else {
                target = scrollTop > videoHeight / 2 ? videoHeight : 0;
            }

            scrollEl.scrollTo({ top: target, behavior: 'smooth' });
        };

        const onTouchEnd = () => {
            touchActive = false;
            snapLandscapeScroll();
            settleToTop();
        };

        contentRef.current?.getScrollElement().then((el) => {
            if (disposed) {
                return;
            }

            scrollEl = el;
            syncAtTopMarker();
            el.addEventListener('scroll', onScroll, { passive: true });
            el.addEventListener('touchstart', onTouchStart, { passive: true });
            el.addEventListener('touchend', onTouchEnd, { passive: true });
            el.addEventListener('touchcancel', onTouchEnd, { passive: true });
        });

        return () => {
            disposed = true;
            window.clearTimeout(settleTimer);
            scrollEl?.removeEventListener('scroll', onScroll);
            scrollEl?.removeEventListener('touchstart', onTouchStart);
            scrollEl?.removeEventListener('touchend', onTouchEnd);
            scrollEl?.removeEventListener('touchcancel', onTouchEnd);
        };
    }, []);

    // Check if the user is an admin
    const currentUserQuery = useCurrentUserQuery();
    const isAdmin = currentUserQuery.data?.userLevel === 'ADMIN';

    // Check if the device belongs to the user
    const isUserDevice = !!devicesQuery.data?.pages.flat().find(device => device.deviceId === deviceId);

    // frameCount is only written once the device reports the event finished,
    // so a missing frameCount means the event is still happening now
    const isLiveEvent = !!eventQuery.data && eventQuery.data.frameCount == null && !eventQuery.data.deletedAt;

    const deviceEventsQuery = useDeviceEventsQuery(deviceId, undefined, RELATED_EVENTS_PAGE_SIZE, isUserDevice);

    const posterUrl = useMemo(() => (
        `${OCGatewayClient.apiURL}/events/${deviceId}/${eventId}/${eventQuery?.data?.posterFrameIndex || 0}?t=${eventQuery.data?.accessToken}`
    ), [deviceId, eventId, eventQuery.data?.accessToken, eventQuery.data?.posterFrameIndex]);

    const videoSourceUrl = useMemo(() => (
        `${OCGatewayClient.apiURL}/videos/${deviceId}/${eventId}?t=${eventQuery.data?.accessToken}`
    ), [deviceId, eventId, eventQuery.data?.accessToken]);

    const syncCurrentFrame = useCallback(() => {
        if (!videoMedia) {
            return;
        }

        const frameCount = eventSummaryQuery.data?.frameCount ?? eventQuery.data?.frameCount;
        // floor, not round: matches the frame actually displayed at currentTime
        const rawFrameIndex = Math.max(0, Math.floor((videoMedia.currentTime || 0) * EVENT_VIDEO_FPS));
        const nextFrameIndex = frameCount ? Math.min(rawFrameIndex, Math.max(0, frameCount - 1)) : rawFrameIndex;

        setCurrentFrameIndex((prevFrameIndex) => prevFrameIndex === nextFrameIndex ? prevFrameIndex : nextFrameIndex);
    }, [videoMedia, eventSummaryQuery.data?.frameCount, eventQuery.data?.frameCount]);

    useEffect(() => {
        if (!videoMedia) {
            return;
        }

        const syncVideoState = () => {
            setIsVideoPaused(videoMedia.paused);
            syncCurrentFrame();
        };

        const events = ['timeupdate', 'seeking', 'seeked', 'loadedmetadata', 'play', 'pause', 'ended'] as const;
        events.forEach((eventName) => videoMedia.addEventListener(eventName, syncVideoState));
        syncVideoState();

        return () => {
            events.forEach((eventName) => videoMedia.removeEventListener(eventName, syncVideoState));
        };
    }, [videoMedia, syncCurrentFrame]);

    useEffect(() => {
        if (!videoMedia) {
            return;
        }

        let retryTimeout: number | undefined;

        const reloadSource = () => {
            window.clearTimeout(retryTimeout);
            retryTimeout = window.setTimeout(() => {
                const shouldResume = !videoMedia.paused;
                videoMedia.load();
                if (shouldResume) {
                    videoMedia.play().catch(() => undefined);
                } else {
                    // load() re-arms the autoplay attribute; pause() clears the
                    // autoplaying flag so a paused video stays paused after reload
                    videoMedia.pause();
                }
            }, 5000);
        };

        videoMedia.addEventListener('error', reloadSource);

        return () => {
            window.clearTimeout(retryTimeout);
            videoMedia.removeEventListener('error', reloadSource);
        };
    }, [videoMedia, videoSourceUrl]);

    const copyDeviceId = () => {
        presentResult(async () => {
            await navigator.clipboard.writeText(deviceId);
        }, t('device.device_id_copied'));
    }

    const unlock = async () => {
        presentResult(() => OCGatewayClient.request('runDeviceCommand', { deviceId, command: 'unlock' }), t('device.remote_unlock_requested'));
    };

    const [presentDevicePopover, dismissDevicePopover] = useIonPopover(DevicePopover, {
        deviceId,
        onCopyDeviceId: () => copyDeviceId(),
        onShowDevice: () => history.push(`/devices/${deviceId}`),
        onDismiss: (data: any, role: string) => dismissDevicePopover(data, role),
    });

    const share = () => {
        Share.share({
            title: t('event.share_event_title'),
            text: t('event.share_event_text'),
            url: `https://onlycat.app/events/${deviceId}/${eventId}?t=${eventQuery.data?.accessToken}`,
            dialogTitle: t('event.share_event_dialog_title')
        }).catch(error => console.log(error));
    };

    // Web downloads get the blob path (save via anchor); native gets a cached
    // file handed to the share sheet.
    const downloadAndShare = async (options: {
        url: string;
        filename: string;
        loadingMessage: string;
        shareTitle: string;
        shareText: string;
        shareDialogTitle: string;
        errorHeader: string;
        errorMessage: string;
    }) => {
        await presentLoading({
            message: options.loadingMessage,
            backdropDismiss: false,
            spinner: 'dots',
        });

        try {
            const result = await Filesystem.downloadFile({
                url: options.url,
                path: options.filename,
                directory: Directory.Cache
            });

            if (result.blob) {
                const blobUrl = URL.createObjectURL(result.blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = options.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
            }
            else if (result.path) {
                Share.share({
                    title: options.shareTitle,
                    text: options.shareText,
                    files: ['file://' + result.path],
                    dialogTitle: options.shareDialogTitle
                });
            }
        }
        catch {
            presentAlert({
                header: options.errorHeader,
                message: options.errorMessage,
                buttons: [tCommon('buttons.ok')]
            });
        }
        finally {
            await dismissLoading();
        }
    };

    const downloadVideo = () => downloadAndShare({
        // Via the onlycat.app distribution (not the gateway directly) so the
        // on-demand MP4 mux is served from CloudFront's edge cache.
        url: `https://onlycat.app/sharing/video/${deviceId}/${eventId}?t=${eventQuery.data?.accessToken}`,
        filename: `${deviceId}-${eventId}.mp4`,
        loadingMessage: t('event.downloading_video'),
        shareTitle: t('event.share_video_title'),
        shareText: t('event.share_video_text'),
        shareDialogTitle: t('event.share_video_dialog_title'),
        errorHeader: t('event.download_error'),
        errorMessage: t('event.download_error_message')
    });

    const downloadFrame = () => downloadAndShare({
        url: `https://onlycat.app/sharing/frame/${deviceId}/${eventId}/${currentFrameIndex}?t=${eventQuery.data?.accessToken}`,
        filename: `${deviceId}-${eventId}-frame-${currentFrameIndex}.jpg`,
        loadingMessage: t('event.downloading_frame'),
        shareTitle: t('event.share_frame_title'),
        shareText: t('event.share_frame_text'),
        shareDialogTitle: t('event.share_frame_dialog_title'),
        errorHeader: t('event.download_frame_error'),
        errorMessage: t('event.download_frame_error_message')
    });

    const deleteEvent = () => {
        presentAlert({
            header: t('event.delete_event_alert_header'),
            subHeader: t('event.delete_event_alert_subheader'),
            message: t('event.delete_event_alert_message'),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: tCommon('buttons.delete'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            async () => {
                                await deleteEventMutation.mutateAsync({ deviceId, eventId });
                                // replace, not push: back must not return to the
                                // deleted event's URL and reopen its modal
                                history.replace(clearEventModalHref(history.location));
                            },
                            t('event.event_deleted_successfully')
                        );
                    }
                }
            ]
        });
    };

    const toggleStar = () => {
        const isCurrentlyStarred = starredQuery.data?.isStarred;

        if (isCurrentlyStarred) {
            presentResult(
                () => unstarEventMutation.mutateAsync({ deviceId, eventId }),
                t('event.event_unstarred')
            );
        } else {
            presentResult(
                () => starEventMutation.mutateAsync({ deviceId, eventId }),
                t('event.event_starred')
            );
        }
    };

    const [presentSharePopover, dismissSharePopover] = useIonPopover(SharePopover, {
        isVideoPaused,
        onShare: () => share(),
        onDownloadVideo: () => downloadVideo(),
        onDownloadFrame: () => downloadFrame(),
        onDismiss: (data: any, role: string) => dismissSharePopover(data, role),
    });

    const [presentMoreOptionsPopover, dismissMoreOptionsPopover] = useIonPopover(MoreOptionsPopover, {
        isLiveEvent,
        canManage: isUserDevice || isAdmin,
        onUnlock: () => unlock(),
        onShowDevice: () => history.push(`/devices/${deviceId}`),
        onDeleteEvent: () => deleteEvent(),
        onDismiss: (data: any, role: string) => dismissMoreOptionsPopover(data, role),
    });

    const timelineSubevents = eventSummaryQuery.data?.subevents ?? NO_SUBEVENTS;
    const activeTimelineIndex = useMemo(() => {
        if (!timelineSubevents.length) {
            return -1;
        }

        // Highlight the most recent subevent that has started, so the playhead
        // always maps to a row; before the first subevent, highlight the first.
        for (let index = timelineSubevents.length - 1; index > 0; index--) {
            if (currentFrameIndex >= timelineSubevents[index].startFrameIndex) {
                return index;
            }
        }

        return 0;
    }, [timelineSubevents, currentFrameIndex]);

    useEffect(() => {
        setCurrentFrameIndex(0);
    }, [deviceId, eventId]);

    // one formatter for every seconds figure, so the timeline rows and the
    // duration in the header can't drift apart in locale or precision
    const formatSeconds = useCallback((seconds: number) => {
        const formattedCount = new Intl.NumberFormat(activeLocale, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).format(seconds);

        return tCommon('time.duration_seconds_decimal', { formattedCount });
    }, [activeLocale, tCommon]);

    const formatTimelineStart = (startFrameIndex: number) => formatSeconds(startFrameIndex / EVENT_VIDEO_FPS);

    const formatSubeventLabel = (action: string, direction: string) => {
        const subeventKey = `${action.toUpperCase()}_${direction.toUpperCase()}`;

        return t(`event.timeline.subevents.${subeventKey}`, {
            defaultValue: `${action.toLowerCase()} ${direction.toLowerCase()}`
        });
    };

    const jumpToTimelineSubevent = useCallback((startFrameIndex: number) => {
        if (!videoMedia) {
            return;
        }

        videoMedia.currentTime = frameCenterTime(Math.max(0, startFrameIndex));
        setCurrentFrameIndex(startFrameIndex);
    }, [videoMedia]);

    const timelineDuration = useMemo(() => {
        if (!eventQuery.data?.frameCount) {
            return '-';
        }

        return formatSeconds(Math.max(0, eventQuery.data.frameCount / EVENT_VIDEO_FPS));
    }, [eventQuery.data?.frameCount, formatSeconds]);

    const eventTimestampLabel = useMemo(() => {
        if (!eventQuery.data?.timestamp) {
            return '-';
        }

        const timestamp = new Date(eventQuery.data.timestamp);
        if (Number.isNaN(timestamp.getTime())) {
            return '-';
        }

        return formatEventDateTime(timestamp);
    }, [eventQuery.data?.timestamp]);

    const deviceLabel = deviceQuery.data?.description ?? deviceId;
    const isStarred = !!starredQuery.data?.isStarred;

    // Neighbouring events (same device, newest first) for the bottom carousel
    const deviceEvents = useMemo(() => (
        ((deviceEventsQuery.data?.pages.flat() ?? []) as DeviceEvent[])
            .filter((event) => !event.deletedAt)
    ), [deviceEventsQuery.data]);
    const currentEventIndex = deviceEvents.findIndex((event) => event.eventId === eventId);
    // same threshold as the short-viewport rules in EventModal.module.css:
    // landscape phones have no room for a fixed strip over the content
    const isShortViewport = useMediaQuery('(max-height: 599px)');
    const showCarousel = isUserDevice && currentEventIndex !== -1 && deviceEvents.length > 1 && !isShortViewport;

    return (
        <IonPage>
            {/* mode is inherited from the closest ancestor element carrying the
                attribute, so the forced-iOS modal shell would otherwise restyle
                everything inside; re-pin md here to keep the content native on
                Android. Spread because ion-content itself isn't mode-styled and
                @ionic/react doesn't type the prop, yet the attribute still
                drives inheritance for every component within. */}
            <IonContent ref={contentRef} fullscreen {...(FORCE_CARD_MODAL ? { mode: 'md' } : null)} className={showCarousel ? styles.contentWithCarousel : undefined}>
                {/* sticky on phones so only the detail scrolls under it; on desktop
                    it scrolls away with the rest of the page */}
                <div className={styles.videoBlock} ref={videoBlockRef}>
                    <div className={styles.backButtonContainer}>
                        <IonButton
                            className={styles.backButton}
                            onClick={onClose}
                            aria-label={tCommon('buttons.dismiss')}
                        >
                            <IonIcon slot="icon-only" icon={chevronDownOutline} />
                        </IonButton>
                    </div>
                    <div className={styles.videoStage}>
                        {eventQuery.isSuccess && (
                            <EventVideoPlayer.Provider>
                                <EventVideoSkin
                                    currentFrameIndex={currentFrameIndex}
                                    frameCount={eventSummaryQuery.data?.frameCount ?? eventQuery.data?.frameCount}
                                    poster={posterUrl}
                                >
                                    <HlsJsVideo
                                        src={videoSourceUrl}
                                        config={HLS_VIDEO_CONFIG}
                                        streamType="on-demand"
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="auto"
                                        disablePictureInPicture
                                    />
                                    <EventVideoMediaBridge onMediaChange={setVideoMedia} />
                                </EventVideoSkin>
                            </EventVideoPlayer.Provider>
                        )}
                    </div>
                </div>

                {/* detailColumn: in landscape the detail matches the video's
                    width, centred over the black page — full width elsewhere */}
                {eventQuery.isLoading && (
                    <div className={styles.detailColumn}>
                        <LoadingStateContainer />
                    </div>
                )}

                {eventQuery.isError && (
                    <div className={styles.detailColumn}>
                        <LoadFailureStateContainer
                            subtitle={t('event.couldnt_load_event_subtitle')}
                            error={eventQuery.error}
                            onRetry={() => eventQuery.refetch()}
                        />
                    </div>
                )}

                {eventQuery.isSuccess && (
                    <div className={styles.detailColumn}>
                        <div className={styles.titleBlock}>
                            <div className={styles.titleRow}>
                                <EventSummaryDescription
                                    className={styles.eventTitle}
                                    deviceId={deviceId}
                                    eventSummary={eventSummaryQuery.data}
                                    isLoading={eventSummaryQuery.isLoading}
                                    locale={activeLocale}
                                    skeletonStyle={{ width: '180px' }}
                                    unknownLabel={t('event.event_number', { eventId })}
                                />
                                <div className={styles.titleChip}>
                                    <EventClassificationChip deviceId={deviceId} eventId={eventId} accessToken={accessToken} />
                                </div>
                            </div>
                            <p className={`${styles.metaLine} ${styles.metaLinePrimary}`}>
                                <IonText
                                    className={styles.metaDevice}
                                    onClick={(e: any) => presentDevicePopover({
                                        event: e,
                                        dismissOnSelect: true,
                                        cssClass: styles.popover
                                    })}
                                >
                                    {deviceLabel}
                                </IonText>
                                {` #${eventId}`}
                            </p>
                            <p className={styles.metaLine}>{eventTimestampLabel}</p>
                        </div>

                        <div className={styles.actionsRow}>
                            <IonButton
                                className={styles.actionPill}
                                onClick={(e: any) => presentSharePopover({
                                    event: e,
                                    dismissOnSelect: true,
                                    cssClass: styles.menuPopover
                                })}
                            >
                                <IonIcon slot="start" ios={shareOutline} md={shareSocial} />
                                {t('event.actions.share')}
                                <IonIcon slot="end" icon={chevronDown} className={styles.pillChevron} />
                            </IonButton>
                            <IonButton
                                className={`${styles.actionPill} ${styles.starPill} ${isStarred ? styles.actionPillActive : ''}`}
                                disabled={!eventQuery.isSuccess}
                                onClick={toggleStar}
                                aria-label={isStarred ? t('event.unstar_event') : t('event.star_event')}
                            >
                                <IonIcon slot="start" icon={isStarred ? star : starOutline} />
                                {isStarred ? t('event.actions.starred') : t('event.actions.star')}
                            </IonButton>
                            <IonButton
                                className={styles.actionPill}
                                onClick={(e: any) => presentMoreOptionsPopover({
                                    event: e,
                                    dismissOnSelect: true,
                                    cssClass: styles.menuPopover
                                })}
                            >
                                {t('event.actions.more')}
                                <IonIcon slot="end" icon={chevronDown} className={styles.pillChevron} />
                            </IonButton>
                        </div>

                        <div className={styles.timelineCard}>
                            <IonList className={styles.timelineList}>
                                <IonItemDivider className={styles.timelineDivider}>
                                    <div className={styles.timelineDividerContent}>
                                        <span>{t('event.timeline.title')}</span>
                                        <span className={styles.timelineDividerMeta}>
                                            <span>{timelineDuration}</span>
                                        </span>
                                    </div>
                                </IonItemDivider>
                                {eventSummaryQuery.isLoading && (
                                    <IonItem className={styles.timelineStateItem} aria-label={t('event.timeline.loading')}>
                                        <IonLabel>
                                            <IonSkeletonText animated style={{ width: '65%' }} />
                                        </IonLabel>
                                    </IonItem>
                                )}
                                {eventSummaryQuery.isError && (
                                    <IonItem className={styles.timelineStateItem}>
                                        <IonLabel>{t('event.timeline.failed_to_load', { error: eventSummaryQuery.error.message })}</IonLabel>
                                    </IonItem>
                                )}
                                {!eventSummaryQuery.isLoading && !eventSummaryQuery.isError && !timelineSubevents.length && (
                                    <IonItem className={styles.timelineStateItem}>
                                        <IonLabel>{t('event.timeline.empty')}</IonLabel>
                                    </IonItem>
                                )}
                                {timelineSubevents.map((subevent, index) => (
                                    <IonItem
                                        key={`${subevent.startFrameIndex}-${subevent.endFrameIndex}-${index}`}
                                        button={true}
                                        detail={false}
                                        onClick={() => jumpToTimelineSubevent(subevent.startFrameIndex)}
                                        className={`${styles.timelineEntry} ${index === 0 ? styles.timelineEntryFirst : ''} ${index === timelineSubevents.length - 1 ? styles.timelineEntryLast : ''} ${index === activeTimelineIndex ? styles.timelineEntryActive : ''}`}
                                    >
                                        <div slot="start" className={styles.timelineLeading}>
                                            <span className={styles.timelineTimestamp}>
                                                {formatTimelineStart(subevent.startFrameIndex)}
                                            </span>
                                            <div className={styles.timelineGraphic} aria-hidden="true">
                                                <span className={styles.timelineMarker} />
                                            </div>
                                        </div>
                                        <IonLabel className={styles.timelineItem}>
                                            <div className={styles.timelineTitle}>
                                                {subevent.rfidCode && <RfidChip rfidCode={subevent.rfidCode} deviceId={deviceId} />}
                                                <p>{formatSubeventLabel(subevent.action, subevent.direction)}</p>
                                            </div>
                                        </IonLabel>
                                    </IonItem>
                                ))}
                            </IonList>
                        </div>

                        {eventQuery.data?.deletedAt && (
                            <IonCard color="danger">
                                <IonCardHeader>
                                    <IonCardTitle>{t('event.deleted_event')}</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonLabel>
                                        {t('event.deleted_event_message', {
                                            time: formatEventDateTime(new Date(eventQuery.data.deletedAt))
                                        })}
                                    </IonLabel>
                                </IonCardContent>
                            </IonCard>
                        )}

                    </div>
                )}

                {/* outside the query gates so the bar stays put while an event loads */}
                {showCarousel && (
                    <EventNavCarousel
                        events={deviceEvents}
                        currentIndex={currentEventIndex}
                        // event views always replace so scrubbing can't stack a
                        // history entry per event
                        onNavigate={(nextEventId) => history.replace(eventModalHref(history.location, deviceId, nextEventId))}
                        canLoadOlder={deviceEventsQuery.hasNextPage && !deviceEventsQuery.isFetchingNextPage}
                        onLoadOlder={() => deviceEventsQuery.fetchNextPage()}
                    />
                )}
            </IonContent>
        </IonPage >
    );
};

const MemoEventModal = memo(EventModal);

interface PresentedEvent {
    deviceId: string;
    eventId: number;
    accessToken: string | null;
}

/**
 * Presents the event modal whenever the location names an event (see
 * utils/eventModalLocation): the canonical /events/... path form over the
 * activity feed — whose route also matches /events, keeping the feed mounted
 * underneath — or an `event` search param over any other page, which never
 * changes the matched route, so the origin page keeps its scroll behind the
 * modal. Shared URLs, deep links, and push notifications all use the path
 * form and land straight in the modal.
 *
 * Watches the shared history object directly rather than the router context:
 * the presenter renders no routed views, and IonReactRouter's location context
 * is unreliable outside its outlet.
 */
export const EventModalPresenter: React.FC = () => {
    const [location, setLocation] = useState(appHistory.location);
    const match = matchEventModalLocation(location);
    const [ready, setReady] = useState(false);
    const [presented, setPresented] = useState<PresentedEvent | null>(null);
    const [presentingElement, setPresentingElement] = useState<HTMLElement | undefined>(undefined);

    useEffect(() => {
        // utils/history is history@5: listeners receive an { action, location } update
        return appHistory.listen(({ location: nextLocation }) => setLocation(nextLocation));
    }, []);

    // card presentation scales the page behind the modal, so it needs the shell's
    // router outlet as its presenting element
    useEffect(() => {
        setPresentingElement(document.getElementsByTagName('ion-router-outlet')[0] as HTMLElement | undefined);
    }, []);

    // Cold starts land directly on an event URL with nothing rendered behind
    // the modal; give the shell a beat to mount a page for the card
    // presentation to sit on.
    useEffect(() => {
        if (!matchEventModalLocation(appHistory.location)) {
            setReady(true);
            return;
        }

        const timeout = window.setTimeout(() => setReady(true), 50);
        return () => window.clearTimeout(timeout);
    }, []);

    useEffect(() => {
        if (!match) {
            return;
        }

        setPresented({
            deviceId: match.deviceId,
            eventId: match.eventId,
            accessToken: match.accessToken
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const close = useCallback(() => {
        // replace, not push: the modal never stacked an entry, closing just
        // restores the origin page's own location
        appHistory.replace(clearEventModalHref(appHistory.location));
    }, []);

    const handleDidDismiss = useCallback(() => {
        // swipe/hardware-back dismissal bypasses close(); re-sync the URL
        if (matchEventModalLocation(appHistory.location)) {
            close();
        }

        // unmount the content so the video player doesn't outlive the modal
        setPresented(null);
    }, [close]);

    return (
        <IonModal
            isOpen={ready && !!match}
            onDidDismiss={handleDidDismiss}
            presentingElement={presentingElement}
            mode={FORCE_CARD_MODAL ? 'ios' : undefined}
            className={styles.eventModal}
        >
            {presented && (
                <MemoEventModal
                    deviceId={presented.deviceId}
                    eventId={presented.eventId}
                    accessToken={presented.accessToken}
                    onClose={close}
                />
            )}
        </IonModal>
    );
};

export default MemoEventModal;
